import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IGeoJSONPoint, IUbicacionBase } from 'modelos/src';
import { retry, Subscription, throwError, timer } from 'rxjs';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { ClienteJerarquiaService } from '../../../../../../auxiliares/servicios/cliente-jerarquia.service';
import {
  MapCardComponent,
  MapCardConfig,
} from '../../../../../../componentes/map-card/map-card.component';
import { PIEZAS_DETALLE } from '../../../../../../componentes/detalle/detalle.components';
import { CrearEditarHospitalComponent } from '../crear-editar-hospital/crear-editar-hospital.component';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';

type IHospital = IUbicacionBase<'Hospital'>;

@Component({
  selector: 'app-detalles-hospital',
  imports: [AuxiliaresModule, MapCardComponent, ...PIEZAS_DETALLE],
  templateUrl: './detalles-hospital.component.html',
  styleUrls: [
    '../../../../../../componentes/detalle/detalle-pagina.scss',
    '../../detalle-emergencias.scss',
  ],
})
export class DetallesHospitalComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private formularios = inject(FormularioDialogService);
  private lista2 = inject(Lista2Service);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  helper = inject(HelperService);

  public loading = true;
  public id = '';
  public hospital?: IHospital;
  private hospital$?: Subscription;
  private params$?: Subscription;

  public mapCardConfig?: MapCardConfig;
  /**
   * El cliente, sin HTML. Se calcula al dibujar: el índice de clientes de la
   * jerarquía puede llegar después que la entidad (al recargar la página el
   * chip quedaba vacío).
   */
  get cliente(): string | undefined {
    if (HelperService.getTipoCliente() === 'Final') return undefined;
    const e = this.hospital;
    const cadena = this.clienteJerarquia.cadena(e?.cliente ?? e?.idCliente);
    return cadena[cadena.length - 1]?.nombre || e?.cliente?.nombre || undefined;
  }
  public fechaAlta?: string;

  public puedeEditar = this.helper.tienePermiso(
    'Emergencias Médicas - Editar hospitales',
  );

  private listar(): void {
    this.hospital$?.unsubscribe();
    this.hospital$ = this.lista2
      .ubicacion<'Hospital'>(this.id, 30)
      // Un request que falla terminaba el refresco de cada 30 s para siempre.
      // Si todavía no se cargó nunca, el error sigue de largo.
      .pipe(
        retry({
          delay: (error) =>
            this.hospital ? timer(30000) : throwError(() => error),
        }),
      )
      .subscribe({
        next: (data) => {
          if (!HelperService.cambiaronLosDatos(this.hospital, data)) return;
          this.hospital = data as IHospital;
          this.armar();
          this.loading = false;
        },
        error: (error) => {
          console.error(error);
          this.helper.notifError(error);
          // No existe o no hay acceso: sin esto quedaba el spinner para siempre
          this.loading = false;
        },
      });
  }

  private armar(): void {
    const h = this.hospital;
    if (!h) return;
    this.fechaAlta = h.fechaCreacion
      ? HelperService.getFechaYHora(h.fechaCreacion)
      : undefined;

    const coor = (h.geojson as IGeoJSONPoint)?.coordinates;
    this.mapCardConfig = coor
      ? {
          title: 'Ubicación',
          subtitle: HelperService.extraerDireccion(h.direccion ?? ''),
          icon: 'location_on',
          color: 'red',
          mapOptions: {
            markers: [
              {
                label: 'hospital',
                coor,
                id: h._id,
                centrar: true,
                data: h,
              },
            ],
            center: coor,
            zoom: 16,
          },
        }
      : undefined;
  }

  /** Edita en diálogo; al guardar se recarga el detalle. */
  public editar(): void {
    const ref = this.formularios.abrir(CrearEditarHospitalComponent, {
      tamano: 'mediano',
      data: { edit: this.hospital },
    });
    ref.afterClosed().subscribe((guardado) => {
      if (guardado) this.listar();
    });
  }

  ngOnInit(): void {
    // Al cambiar el id de la ruta Angular reutiliza el componente
    this.params$ = this.route.paramMap.subscribe((params) => {
      this.id = params.get('id') ?? '';
      this.loading = true;
      this.hospital = undefined;
      this.listar();
    });
  }

  ngOnDestroy(): void {
    this.hospital$?.unsubscribe();
    this.params$?.unsubscribe();
  }
}
